// /var/www/afra/192.testproject.work/maracar8/0.8.2/myapp/src/components/mlmOrg/MessageDialog.js
import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from 'primereact/checkbox';

import { API_URLS } from '../../api/api';
import { getSelectedLanguage } from '../../api/api';    // เพิ่ม
import { languageManager } from '../../utils/loadLanguage';  // เพิ่ม

/**
 * MessageDialog:
 * - เมื่อเปิด visible + มี userId => ดึง SPONSOR_VIEW => แก้ไข contact ได้
 * - ผู้ใช้เลือกช่องทางการส่ง (Telephone, Email, Social Media) & พิมพ์ข้อความ => กด "Send"
 */
const MessageDialog = ({ visible, onHide, userId }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // ข้อมูลติดต่อ
    const [telephone, setTelephone] = useState('');
    const [email, setEmail] = useState('');
    const [socialMedia, setSocialMedia] = useState('');

    // ช่องทางที่ผู้ใช้เลือก
    const [sendVia, setSendVia] = useState({
        telephone: false,
        email: false,
        socialMedia: false
    });

    // ข้อความที่จะส่ง
    const [messageText, setMessageText] = useState('');

    // state เก็บข้อความจาก JSON: orgChartComponent
    const [t, setT] = useState({});

    // โหลดภาษา
    useEffect(() => {
        async function loadLang() {
            const lang = getSelectedLanguage(); 
            await languageManager.loadLanguage(lang);
            setT(languageManager.translations.orgChartComponent || {});
        }
        loadLang();
    }, []);

    useEffect(() => {
        if (visible && userId) {
            fetchSponsorData(userId);
        }
    }, [visible, userId]);

    const fetchSponsorData = async (uId) => {
        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem('token');
            const resp = await fetch(API_URLS.SPONSOR_VIEW(uId), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!resp.ok) {
                throw new Error(`Failed to fetch sponsor data (status: ${resp.status})`);
            }
            const data = await resp.json();
            if (Array.isArray(data) && data.length > 0) {
                const row = data[0];
                setTelephone(row.Telephone || '');
                setEmail(row.email || '');
                setSocialMedia(row.ssSocialMedia || '');
            } else {
                setTelephone('');
                setEmail('');
                setSocialMedia('');
            }
        } catch (err) {
            console.error('fetchSponsorData error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const onSendViaChange = (e) => {
        setSendVia({ ...sendVia, [e.target.name]: e.checked });
    };

    const handleSendMessage = async () => {
        try {
            setLoading(true);
            setError(null);

            // ตรวจสอบว่ามีการเลือกช่องทาง
            if (!sendVia.telephone && !sendVia.email && !sendVia.socialMedia) {
                throw new Error(t.errorNoMethod || 'Please select at least one method to send the message');
            }
            // ตรวจสอบว่าข้อความไม่ว่าง
            if (!messageText.trim()) {
                throw new Error(t.errorEmptyMsg || 'Message cannot be empty');
            }

            const selectedMethods = [];
            if (sendVia.telephone) selectedMethods.push({ method: 'Telephone', value: telephone });
            if (sendVia.email) selectedMethods.push({ method: 'Email', value: email });
            if (sendVia.socialMedia) selectedMethods.push({ method: 'Social Media', value: socialMedia });

            console.log('Sending message to userId=', userId);
            console.log('Message text=', messageText);
            console.log('Selected methods=', selectedMethods);

            // แสดง Alert (ตัวอย่าง)
            alert(t.sentMessageAlert || 'Message sent (demo)!');

            // Reset
            setMessageText('');
            setSendVia({ telephone: false, email: false, socialMedia: false });
            onHide();
        } catch (err) {
            console.error('handleSendMessage error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const footer = (
        <div style={{ textAlign: 'right' }}>
            <Button
                label="Close"
                className="p-button-text p-button-danger"
                onClick={onHide}
                disabled={loading}
                icon="pi pi-times"
            />
            <Button
                label="Send"
                className="p-button-text p-button-success"
                onClick={handleSendMessage}
                disabled={loading}
                icon="pi pi-send"
            />
        </div>
    );

    return (
        <Dialog
            // ใช้ t.sendMsgHeader, fallback => "Send Message"
            header={t.sendMsgHeader || "Send Message"}
            visible={visible}
            style={{ width: '30vw' }}
            onHide={onHide}
            footer={footer}
        >
            {loading && <div>{t.loading || "LOADING..."}</div>}
            {error && <div style={{ color: 'red' }}>{error}</div>}

            <div className="p-fluid">
                <h4>{t.contactInfoTitle || "Contact Info"}</h4>
                <div className="field">
                    <label>{(t.telephoneLabel || "Telephone") + ":"}</label>
                    <InputText
                        value={telephone}
                        onChange={(e) => setTelephone(e.target.value)}
                        placeholder={t.telephonePlaceholder || "Enter telephone number"}
                    />
                </div>
                <div className="field">
                    <label>{(t.emailLabel || "Email") + ":"}</label>
                    <InputText
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t.emailPlaceholder || "Enter email address"}
                    />
                </div>
                <div className="field">
                    <label>{(t.socialLabel || "Social Media") + ":"}</label>
                    <InputText
                        value={socialMedia}
                        onChange={(e) => setSocialMedia(e.target.value)}
                        placeholder={t.socialPlaceholder || "Enter social media handle"}
                    />
                </div>

                <h4>{t.sendViaTitle || "Send Via"}</h4>
                <div className="field" style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', gap: '20px' }}>
                        <div>
                            <Checkbox
                                inputId="sendViaTelephone"
                                name="telephone"
                                onChange={onSendViaChange}
                                checked={sendVia.telephone}
                            />
                            <label htmlFor="sendViaTelephone" style={{ marginLeft: '8px' }}>
                                {t.sendViaTelephone || "Telephone"}
                            </label>
                        </div>
                        <div>
                            <Checkbox
                                inputId="sendViaEmail"
                                name="email"
                                onChange={onSendViaChange}
                                checked={sendVia.email}
                            />
                            <label htmlFor="sendViaEmail" style={{ marginLeft: '8px' }}>
                                {t.sendViaEmail || "Email"}
                            </label>
                        </div>
                        <div>
                            <Checkbox
                                inputId="sendViaSocialMedia"
                                name="socialMedia"
                                onChange={onSendViaChange}
                                checked={sendVia.socialMedia}
                            />
                            <label htmlFor="sendViaSocialMedia" style={{ marginLeft: '8px' }}>
                                {t.sendViaSocial || "Social Media"}
                            </label>
                        </div>
                    </div>
                </div>

                <h4>{t.msgTitle || "Message"}</h4>
                <div className="field">
                    <label>{t.msgLabel || "Text"}</label>
                    <InputTextarea
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        placeholder={t.msgPlaceholder || "Enter your message here..."}
                        rows={5}
                        cols={30}
                    />
                </div>
            </div>
        </Dialog>
    );
};

export default MessageDialog;
